<template>
  <div class="ad-insights-panel">
    <div v-if="loading" class="ad-insights__state">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-3">Lade Anzeigen-Daten…</div>
    </div>

    <div v-else-if="error" class="ad-insights__state">
      <v-icon color="error" large>mdi-alert-circle-outline</v-icon>
      <div class="mt-3">Anzeigen-Daten konnten nicht geladen werden.</div>
      <v-btn class="mt-3" small outlined color="primary" @click="load">Erneut versuchen</v-btn>
    </div>

    <template v-else-if="activeChannels.length">
      <!-- Call-to-Action Board -->
      <div class="ad-insights__section-title">Was ist zu tun?</div>

      <div v-if="actionCards.length" class="ad-insights__actions">
        <div v-for="(card, index) in actionCards" :key="index" class="ad-insights__action" :class="`ad-insights__action--${card.type}`">
          <v-icon small :color="card.iconColor">{{ card.icon }}</v-icon>
          <div class="ad-insights__action-info">
            <div class="ad-insights__action-title">{{ card.title }}</div>
            <div class="ad-insights__action-detail">{{ card.detail }}</div>
          </div>
          <v-btn v-if="card.link" icon x-small :href="card.link" target="_blank" title="Im Ads Manager öffnen">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
      </div>
      <div v-else class="ad-insights__hint mt-2">Keine dringenden Auffälligkeiten im gewählten Zeitraum. 👍</div>

      <v-divider class="my-4" />

      <v-tabs v-model="activeTab" grow>
        <v-tab v-for="ch in activeChannels" :key="ch.key">
          {{ ch.label }}
          <v-chip v-if="ch.burners.length" class="ml-2" x-small color="error" dark>{{ ch.burners.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item v-for="ch in activeChannels" :key="ch.key">
          <!-- Top / Flop -->
          <v-row class="mt-1" dense>
            <v-col cols="12" md="6">
              <div class="ad-insights__section-title">Top Anzeigen <span class="ad-insights__hint">(nach ROAS, min. {{ euro(minRankSpend) }} Spend)</span></div>
              <div v-for="ad in ch.top" :key="ad.id" class="ad-insights__rank-row">
                <div class="ad-insights__rank-info">
                  <div class="ad-insights__ellipsis">{{ ad.name }}</div>
                  <div class="ad-insights__campaign ad-insights__ellipsis">{{ ad.campaign }}</div>
                </div>
                <div class="ad-insights__rank-numbers">
                  <span class="ad-insights__roas-chip" :style="{ backgroundColor: roasColor(ad.roas) }">{{ number(ad.roas) }}</span>
                  <span class="ad-insights__spend">{{ euro(ad.spend) }}</span>
                  <v-btn v-if="ad.link" icon x-small :href="ad.link" target="_blank"><v-icon x-small>mdi-open-in-new</v-icon></v-btn>
                </div>
              </div>
              <div v-if="!ch.top.length" class="ad-insights__hint">Keine Anzeigen über der Spend-Schwelle.</div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="ad-insights__section-title">Flop Anzeigen <span class="ad-insights__hint">(nach ROAS, min. {{ euro(minRankSpend) }} Spend)</span></div>
              <div v-for="ad in ch.flop" :key="ad.id" class="ad-insights__rank-row">
                <div class="ad-insights__rank-info">
                  <div class="ad-insights__ellipsis">{{ ad.name }}</div>
                  <div class="ad-insights__campaign ad-insights__ellipsis">{{ ad.campaign }}</div>
                </div>
                <div class="ad-insights__rank-numbers">
                  <span class="ad-insights__roas-chip" :style="{ backgroundColor: roasColor(ad.roas) }">{{ number(ad.roas) }}</span>
                  <span class="ad-insights__spend">{{ euro(ad.spend) }}</span>
                  <v-btn v-if="ad.link" icon x-small :href="ad.link" target="_blank"><v-icon x-small>mdi-open-in-new</v-icon></v-btn>
                </div>
              </div>
              <div v-if="!ch.flop.length" class="ad-insights__hint">Keine Anzeigen über der Spend-Schwelle.</div>
            </v-col>
          </v-row>

          <!-- Kampagnen: Budget vs. Ergebnis -->
          <div class="ad-insights__section-title mt-4">Kampagnen – Budgetanteil vs. Umsatzanteil</div>
          <div v-for="camp in ch.campaigns" :key="camp.name" class="ad-insights__campaign-row">
            <div class="ad-insights__campaign-head">
              <span class="ad-insights__ellipsis">{{ camp.name }}</span>
              <div class="ad-insights__campaign-chips">
                <span class="ad-insights__roas-chip" :style="{ backgroundColor: roasColor(camp.roas) }">ROAS {{ number(camp.roas) }}</span>
                <v-btn v-if="camp.link" icon x-small :href="camp.link" target="_blank"><v-icon x-small>mdi-open-in-new</v-icon></v-btn>
              </div>
            </div>
            <div class="ad-insights__campaign-meta">
              <span v-if="camp.dailyBudget !== null">Budget {{ euro(camp.dailyBudget) }}/Tag</span>
              <span v-if="camp.targetRoas">· Ziel-ROAS {{ number(camp.targetRoas) }}</span>
              <span v-if="camp.status && camp.status !== 'ACTIVE'" class="ad-insights__paused">· {{ camp.status }}</span>
            </div>
            <div class="ad-insights__bar-row">
              <span class="ad-insights__bar-label">Spend {{ percent(camp.spendShare) }}</span>
              <div class="ad-insights__bar"><div class="ad-insights__bar-fill ad-insights__bar-fill--spend" :style="{ width: (camp.spendShare * 100) + '%' }"/></div>
            </div>
            <div class="ad-insights__bar-row">
              <span class="ad-insights__bar-label">Umsatz {{ percent(camp.valueShare) }}</span>
              <div class="ad-insights__bar"><div class="ad-insights__bar-fill ad-insights__bar-fill--value" :style="{ width: (camp.valueShare * 100) + '%' }"/></div>
            </div>
          </div>

          <!-- Alle Anzeigen -->
          <v-expansion-panels flat class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0">Alle Anzeigen ({{ ch.ads.length }})</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  dense
                  mobile-breakpoint="0"
                  :headers="tableHeaders"
                  :items="ch.ads"
                  :items-per-page="15"
                  sort-by="spend"
                  sort-desc
                >
                  <template v-slot:item.name="{ item }">
                    <a v-if="item.link" :href="item.link" target="_blank" class="ad-insights__table-link">
                      <div class="ad-insights__ellipsis" style="max-width: 220px">{{ item.name }}</div>
                    </a>
                    <div v-else class="ad-insights__ellipsis" style="max-width: 220px">{{ item.name }}</div>
                    <div class="ad-insights__campaign ad-insights__ellipsis" style="max-width: 220px">{{ item.campaign }}</div>
                  </template>
                  <template v-slot:item.spend="{ item }">{{ euro(item.spend) }}</template>
                  <template v-slot:item.roas="{ item }">
                    <span class="ad-insights__roas-chip" :style="{ backgroundColor: roasColor(item.roas) }">{{ number(item.roas) }}</span>
                  </template>
                  <template v-slot:item.purchases="{ item }">{{ number(item.purchases, 0) }}</template>
                  <template v-slot:item.cpa="{ item }">{{ item.cpa === null ? '–' : euro(item.cpa) }}</template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <div v-else class="ad-insights__state">
      <v-icon large>mdi-tray-remove</v-icon>
      <div class="mt-3">Keine Anzeigen mit Spend im gewählten Zeitraum.</div>
    </div>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import { mapGetters } from "vuex";
import { getDecimalNumberString, getRateColor } from "@/utils";

const BURNER_MIN_SPEND = 15;
const RANK_MIN_SPEND = 10;
const ACTION_MIN_SPEND = 25;

export default {
  name: "AdInsightsPanel",
  props: {
    shopCode: String,
    active: {
      type: Boolean,
      default: false,
    },
    breakEvenRoas: {
      type: Number,
      default: null,
    },
    shopRevenue: {
      type: Number,
      default: null,
    },
    shopProfit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      insights: null,
      activeTab: 0,
      minRankSpend: RANK_MIN_SPEND,
      tableHeaders: [
        { text: "Anzeige", value: "name" },
        { text: "Spend", value: "spend" },
        { text: "ROAS", value: "roas" },
        { text: "Käufe", value: "purchases" },
        { text: "CPA", value: "cpa" },
      ],
    };
  },
  computed: {
    ...mapGetters(["statisticRange"]),
    activeChannels() {
      if (!this.insights) return [];
      const labels = { facebook: "Facebook", google: "Google", tiktok: "TikTok" };

      return Object.entries(this.insights.byChannel)
        .filter(([, data]) => (data.ads || []).length > 0)
        .map(([key, data]) => {
          const ads = data.ads;
          const ranked = ads.filter((ad) => ad.spend >= RANK_MIN_SPEND);
          const byRoasDesc = [...ranked].sort((a, b) => b.roas - a.roas);

          const totalSpend = ads.reduce((sum, ad) => sum + ad.spend, 0);
          const totalValue = ads.reduce((sum, ad) => sum + ad.purchaseValue, 0);

          return {
            key,
            label: labels[key] || key,
            ads,
            burners: ads.filter((ad) => ad.spend >= BURNER_MIN_SPEND && ad.purchases === 0),
            top: byRoasDesc.slice(0, 5),
            flop: [...byRoasDesc].reverse().slice(0, 5),
            campaigns: (data.campaigns || []).map((camp) => ({
              ...camp,
              spendShare: totalSpend ? camp.spend / totalSpend : 0,
              valueShare: totalValue ? camp.purchaseValue / totalValue : 0,
            })),
          };
        });
    },
    actionCards() {
      const cards = [];

      this.activeChannels.forEach((ch) => {
        ch.burners.forEach((ad) => {
          cards.push({
            severity: 1,
            type: "error",
            icon: "mdi-pause-octagon",
            iconColor: "error",
            title: `Pausieren prüfen: „${ad.name}"`,
            detail: `${ch.label}: ${this.euro(ad.spend)} Spend ohne einen einzigen Kauf.`,
            link: ad.link,
          });
        });

        if (this.breakEvenRoas) {
          ch.ads
            .filter((ad) => ad.spend >= ACTION_MIN_SPEND && ad.purchases > 0 && ad.roas < this.breakEvenRoas)
            .forEach((ad) => {
              cards.push({
                severity: 2,
                type: "warning",
                icon: "mdi-trending-down",
                iconColor: "orange darken-2",
                title: `Unter Break-even: „${ad.name}"`,
                detail: `${ch.label}: ROAS ${this.number(ad.roas)} liegt unter eurem Break-even von ${this.number(this.breakEvenRoas)} — dieser Spend (${this.euro(ad.spend)}) verliert nach allen Kosten Geld.`,
                link: ad.link,
              });
            });
        }

        ch.campaigns.forEach((camp) => {
          if (camp.spend < ACTION_MIN_SPEND) return;

          if (camp.targetRoas && camp.roas < camp.targetRoas * 0.8) {
            cards.push({
              severity: 2,
              type: "warning",
              icon: "mdi-target",
              iconColor: "orange darken-2",
              title: `Ziel-ROAS verfehlt: „${camp.name}"`,
              detail: `Ist ${this.number(camp.roas)} vs. Ziel ${this.number(camp.targetRoas)}${camp.dailyBudget !== null ? ` bei ${this.euro(camp.dailyBudget)}/Tag Budget` : ""} — Budget senken oder Gebotsstrategie prüfen.`,
              link: camp.link,
            });
          } else if (camp.roas >= Math.max(camp.targetRoas || 0, this.breakEvenRoas || 0, 2) * 1.3) {
            cards.push({
              severity: 3,
              type: "success",
              icon: "mdi-rocket-launch",
              iconColor: "green darken-1",
              title: `Skalieren prüfen: „${camp.name}"`,
              detail: `ROAS ${this.number(camp.roas)} liegt deutlich über ${camp.targetRoas ? "dem Ziel (" + this.number(camp.targetRoas) + ")" : "Break-even"}${camp.dailyBudget !== null ? ` — Budget aktuell ${this.euro(camp.dailyBudget)}/Tag` : ""}. Mehr Budget könnte profitablen Umsatz freisetzen.`,
              link: camp.link,
            });
          }

          if (camp.spend >= 50 && camp.spendShare - camp.valueShare >= 0.15) {
            cards.push({
              severity: 2,
              type: "warning",
              icon: "mdi-swap-horizontal",
              iconColor: "orange darken-2",
              title: `Umschichten: „${camp.name}"`,
              detail: `${ch.label}: nimmt ${this.percent(camp.spendShare)} des Budgets, liefert aber nur ${this.percent(camp.valueShare)} des Umsatzes.`,
              link: camp.link,
            });
          }
        });
      });

      return cards.sort((a, b) => a.severity - b.severity).slice(0, 8);
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (active) this.load();
      },
    },
    shopCode() {
      if (this.active) this.load();
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = false;
      this.activeTab = 0;
      this.insights = null;
      this.$emit("loaded", null);
      try {
        const getAdInsights = httpsCallable(functions, "getAdInsights", { timeout: 180000 });
        const res = await getAdInsights({
          shopCode: this.shopCode,
          created_at_min: this.statisticRange.min,
          created_at_max: this.statisticRange.max,
        });
        this.insights = res.data;
        this.$emit("loaded", this.insights);
      } catch (e) {
        this.error = true;
      }
      this.loading = false;
    },
    euro(value) {
      return getDecimalNumberString(value, 2) + " €";
    },
    number(value, fraction = 2) {
      return getDecimalNumberString(value, fraction);
    },
    percent(share) {
      return Math.round(share * 100) + " %";
    },
    roasColor(roas) {
      return getRateColor(3.5, 0, roas);
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-insights__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  color: #757575;
}

.ad-insights__section-title {
  font-weight: 700;
  font-size: 14px;
  margin: 12px 0 6px;
}

.ad-insights__hint {
  font-weight: lighter;
  font-size: 12px;
  color: #9e9e9e;
}

.ad-insights__ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ad-insights__actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
}

.ad-insights__action {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid #ececec;

  &--error {
    background-color: rgba(215, 0, 0, 0.05);
    border-color: rgba(215, 0, 0, 0.25);
  }

  &--warning {
    background-color: rgba(255, 152, 0, 0.06);
    border-color: rgba(255, 152, 0, 0.3);
  }

  &--success {
    background-color: rgba(67, 160, 71, 0.06);
    border-color: rgba(67, 160, 71, 0.3);
  }
}

.ad-insights__action-info {
  flex: 1;
  min-width: 0;
}

.ad-insights__action-title {
  font-size: 13px;
  font-weight: 700;
}

.ad-insights__action-detail {
  font-size: 12px;
  color: #616161;
}

.ad-insights__rank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;

  &:nth-child(odd) {
    background-color: #f7f7f7;
  }
}

.ad-insights__rank-info {
  min-width: 0;
  font-size: 13px;
}

.ad-insights__campaign {
  font-size: 11px;
  color: #9e9e9e;
}

.ad-insights__rank-numbers {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.ad-insights__roas-chip {
  display: inline-block;
  color: #2b2b2b;
  font-weight: 700;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;
}

.ad-insights__spend {
  font-size: 12px;
  font-weight: 600;
  min-width: 64px;
  text-align: right;
}

.ad-insights__campaign-row {
  padding: 8px;
  border: 1px solid #ececec;
  border-radius: 8px;
  margin-bottom: 8px;
}

.ad-insights__campaign-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
}

.ad-insights__campaign-chips {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.ad-insights__campaign-meta {
  font-size: 11px;
  color: #757575;
  margin-bottom: 6px;
}

.ad-insights__paused {
  color: #d70000;
  font-weight: 600;
}

.ad-insights__table-link {
  text-decoration: none;
  color: #1c6ebb;
}

.ad-insights__bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
}

.ad-insights__bar-label {
  font-size: 11px;
  color: #757575;
  min-width: 92px;
}

.ad-insights__bar {
  flex: 1;
  height: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.ad-insights__bar-fill {
  height: 100%;
  border-radius: 4px;

  &--spend {
    background-color: #d70000;
    opacity: 0.65;
  }

  &--value {
    background-color: #43a653;
  }
}
</style>
